import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  LinearProgress,
  Collapse,
  Snackbar,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  Smartphone,
  Phone,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle,
  ChevronDown,
  Info as InfoIcon,
  Package,
  Calendar,
  MapPin,
  Hash,
  CheckCircle,
  MessageSquare,
  Zap,
  Monitor,
  Tablet,
} from 'lucide-react';
import { motion } from 'framer-motion';
import ReviewAndConfirm from './ReviewAndConfirm/ReviewAndConfirm';
import SubscriptionAndShipping from './SubscriptionAndShipping';
import Payment from './Payment';
import { trackStarted } from '../../utils/analytics';
import './GetANewNumber.css';

// Enhanced Theme Configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#ff385c',
      light: '#ffeaee',
    },
    secondary: {
      main: '#00a699',
      light: '#e6f7f6',
    },
    info: {
      main: '#6366F1',
      light: '#eef0ff',
    },
    purple: {
      main: '#9333EA',
      light: '#f5ebff',
    },
    error: {
      main: '#ef4444',
      light: '#fee2e2',
    },
    warning: {
      main: '#f59e0b',
      light: '#fef3c7',
    },
    success: {
      main: '#10b981',
      light: '#d1fae5',
    },
    background: {
      default: '#f9fafb',
    },
    text: {
      primary: '#111827',
      secondary: '#6b7280',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
    h5: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],
});

// Enhanced Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(10),
  maxWidth: '800px',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const ProgressIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10,
}));

const SectionContainer = styled(motion.div)(({ theme, borderColor = theme.palette.primary.main, bgColor = theme.palette.primary.light }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: '#FFFFFF',
  borderRadius: theme.shape.borderRadius,
  border: '1px solid #eaeaea',
  borderLeft: `4px solid ${borderColor}`,
  boxShadow: theme.shadows[1],
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: bgColor,
    opacity: 0.08,
    zIndex: 0,
    pointerEvents: 'none',
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
}));

const LineContainer = styled(SectionContainer)(({ theme }) => ({
  borderLeftColor: theme.palette.primary.main,
  '&::before': {
    backgroundColor: theme.palette.primary.light,
  },
}));

const LocationContainer = styled(SectionContainer)(({ theme }) => ({
  borderLeftColor: theme.palette.info.main,
  '&::before': {
    backgroundColor: theme.palette.info.light,
  },
}));

const SectionTitle = styled(Typography)(({ theme, color = theme.palette.primary.main }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  marginBottom: theme.spacing(2),
  color: color,
  textAlign: 'center',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '40px',
    height: '2px',
    backgroundColor: color,
    borderRadius: '1px',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: '#F9FAFB',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 0 0 3px rgba(255, 56, 92, 0.12)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
  '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
    fontWeight: 600,
  },
  '& .MuiInputAdornment-root': {
    '& svg': {
      color: theme.palette.text.secondary,
    },
  },
}));

const ColoredStepIcon = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active ? theme.palette.primary.main : ownerState.completed ? theme.palette.success.main : '#E5E7EB',
  color: '#fff',
  width: 36,
  height: 36,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.3s ease-in-out',
  boxShadow: ownerState.active || ownerState.completed ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none',
}));

const StepConnector = styled(Box)(({ theme, active, completed }) => ({
  flex: 1,
  height: 2,
  backgroundColor: completed ? theme.palette.success.main : active ? theme.palette.primary.main : '#E5E7EB',
  transition: 'background-color 0.3s ease-in-out',
}));

const ActionButton = styled(Button)(({ theme, isback = false }) => ({
  borderRadius: '10px',
  padding: '12px 24px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: isback ? 'none' : theme.shadows[2],
  transition: 'all 0.2s ease-in-out',
  ...(isback
    ? {
      borderColor: '#6b7280',
      color: '#6b7280',
      '&:hover': {
        backgroundColor: '#F3F4F6',
        borderColor: '#4b5563',
      }
    }
    : {
      backgroundColor: '#0066ff',
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: '#0052cc',
        boxShadow: theme.shadows[3],
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: theme.shadows[1],
      }
    }
  ),
}));

const InstructionAccordion = styled(Accordion)(({ theme }) => ({
  marginTop: theme.spacing(3),
  border: '1px solid #eaeaea',
  borderRadius: '10px !important',
  boxShadow: 'none',
  overflow: 'hidden',
  '&::before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '56px',
    padding: '0 16px',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  '& .MuiAccordionSummary-content': {
    gap: '8px',
  },
  '& .MuiAccordionDetails-root': {
    padding: '16px',
    backgroundColor: '#F9FAFB',
  },
}));

const RadioOptionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  background: '#F9FAFB',
  borderRadius: '10px',
  padding: '12px 16px',
  border: '1px solid #eaeaea',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(1),
  '& .MuiFormControlLabel-root': {
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

const EsimInfoBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.primary.light,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.primary.light}`,
}));

const StepContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StepItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
  '& .MuiListItemIcon-root': {
    minWidth: '32px',
  },
}));

const ImeiMethodContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  '& svg': {
    color: theme.palette.primary.main,
    flexShrink: 0,
    marginTop: '3px',
  },
}));

// Since we're removing the "Previous Carrier" step for GetANewNumber
const steps = [
  'Device Compatibility',
  'Location',
  'Subscription & Shipping',
  'Review & Confirm',
  'Payment'
];

function ColoredStepIconComponent(props) {
  const { active, completed, className, icon } = props;
  return (
    <ColoredStepIcon ownerState={{ active, completed }} className={className}>
      {completed ? <Check size={20} /> : icon}
    </ColoredStepIcon>
  );
}

const CustomStepper = ({ activeStep, steps }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel StepIconComponent={ColoredStepIconComponent}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pb: 3 }}>
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ColoredStepIconComponent
              active={activeStep === index}
              completed={activeStep > index}
              icon={index + 1}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: activeStep === index ? 'primary.main' : activeStep > index ? 'success.main' : 'text.secondary',
                fontWeight: activeStep === index ? 600 : 400,
                fontSize: '0.75rem'
              }}
            >
              {label}
            </Typography>
          </Box>
          {index < steps.length - 1 && (
            <StepConnector
              active={activeStep > index}
              completed={activeStep > index + 1}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

const GetANewNumber = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const planData = location.state;

  // State Management
  const [activeStep, setActiveStep] = useState(0);
  const [imei, setImei] = useState('');
  const [imei2, setImei2] = useState('');
  const [esim, setEsim] = useState('no');
  const [zipCode, setZipCode] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [progress, setProgress] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isImeiValid, setIsImeiValid] = useState(true);
  const [reviewData, setReviewData] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState('3months');
  const [shippingMethod, setShippingMethod] = useState('standard');

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  useEffect(() => {
    setProgress((activeStep / (steps.length - 1)) * 100);
  }, [activeStep]);

  useEffect(() => {
    trackStarted(planData?.planData?.carrier?.name); // skipped by trackStarted when unknown
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateIMEI = (imei) => {
    return /^\d{15}$/.test(imei) || imei === '';
  };

  const validateZipCode = (zipCode) => {
    return /^\d{5}(-\d{4})?$/.test(zipCode);
  };

  const validateForm = () => {
    switch (activeStep) {
      case 0:
        return validateIMEI(imei) || imei === '';
      case 1:
        return validateZipCode(zipCode);
      case 2:
        if (esim === 'yes') {
          return subscriptionType !== '';
        }
        return subscriptionType !== '' && shippingMethod !== '';
      case 3:
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleIMEIChange = (event) => {
    const newIMEI = event.target.value;
    setImei(newIMEI);
    setIsImeiValid(validateIMEI(newIMEI));
  };

  const handleNext = () => {
    if (!validateForm()) {
      setSnackbarMessage('Please fill in all required fields correctly.');
      setSnackbarOpen(true);
      return;
    }

    if (activeStep === steps.length - 3) {
      // Extract carrier data
      const carrierData = planData?.planData?.carrier || {};

      // Determine if eSIM is enabled
      const isEsimEnabled = esim === 'yes';

      // Calculate the correct tier price (same logic as SwitchingCarriers.js)
      const circle = planData?.planData?.circle;
      const existingMembers = (circle?.family_size || 0) + (circle?.total_joiners || 0);
      const totalMembers = existingMembers + 1; // Always 1 line for GetANewNumber

      // Get pricing tiers from the plan data
      const pricingTiers = planData?.planData?.selected_plan?.pricing?.pricing_tiers || [];
      // Sort tiers by line count to ensure proper ordering
      const sortedTiers = [...pricingTiers].sort((a, b) => a.line_count - b.line_count);

      // Find the appropriate tier based on total members
      let perLineCost = planData?.planData?.selected_plan?.per_line_cost || 0;
      const tierMatch = sortedTiers.find(tier => tier.line_count >= totalMembers);

      if (tierMatch) {
        perLineCost = tierMatch.price_per_line;
        console.log(`Found pricing tier: ${tierMatch.line_count} lines at ${perLineCost}/line`);
      } else if (sortedTiers.length > 0) {
        // If no matching tier found, use the last tier (highest member count)
        perLineCost = sortedTiers[sortedTiers.length - 1].price_per_line;
        console.log(`Using highest tier price: ${perLineCost}/line`);
      }

      const monthlyRate = perLineCost;
      const taxes = 0;
      const totalMonthlyRate = monthlyRate;

      // Calculate activation fee
      const activationFeePerLine = Number(carrierData.activation_fee_amount || 35.00);
      const totalActivationFee = activationFeePerLine;

      // Calculate subscription costs
      const isAnnual = subscriptionType === 'annual';
      const perLineSubscriptionCost = isAnnual ? 96.00 : 36.00;
      const totalSubscriptionCost = perLineSubscriptionCost;

      // Calculate shipping costs - only if NOT using eSIM
      const isExpedited = shippingMethod === 'expedited';
      const shippingCost = isEsimEnabled ? 0 : (isExpedited ? 15.00 : 8.00);

      // Calculate total due today
      const verizonTotal = totalMonthlyRate + totalActivationFee;
      const circledinTotal = totalSubscriptionCost + shippingCost;
      const totalDueToday = verizonTotal + circledinTotal;

      // FIXED: Use actual features from the selected plan
      const selectedPlan = planData?.planData?.selected_plan || planData?.selected_plan;
      const planFeatures = selectedPlan?.features || [];
      console.log('Using actual plan features:', planFeatures);

      // Prepare the review data with explicit Number conversions for numeric values
      const reviewData = {
        planDetails: {
          circleName: planData?.planData?.circle?.name || "Morning Coffee",
          planName: selectedPlan?.name || "Verizon - Unlimited Welcome",
          planId: selectedPlan?.id,
          monthlyRate: monthlyRate,
          taxes: 0,
          totalMonthly: totalMonthlyRate,
          // Add per line and total activation fee info
          activationFeePerLine: activationFeePerLine,
          totalActivationFee: totalActivationFee,
          totalDueToday: totalDueToday, // Add total due today
          carrier: planData?.planData?.carrier?.name || 'verizon',
          // FIXED: Use actual features from the selected plan
          features: planFeatures.map(feature => ({
            title: feature.title || feature.name,
            description: feature.short_description || feature.description
          }))
        },
        userDetails: {
          type: "new",
          imei: imei,
          esim: isEsimEnabled, // Use the correct eSIM status
          imei2: imei2,
          area_code: areaCode || '',
          zip_code: zipCode || '',
          lineDetails: [{
            imei: imei,
            esim: isEsimEnabled,
            imei2: isEsimEnabled ? imei2 : '',
            lineNumber: 1
          }],
          circle_id: planData?.planData?.circle?.id,
          circle_name: planData?.planData?.circle?.name,
          joining_condition: planData?.joining_condition || "new_number"
        },
        subscriptionDetails: {
          type: subscriptionType,
          shipping: isEsimEnabled ? 'eSIM' : shippingMethod, // Set shipping method accordingly
          // Add subscription cost information
          perLineSubscriptionCost: perLineSubscriptionCost,
          totalSubscriptionCost: totalSubscriptionCost,
          shippingCost: shippingCost // 0 if eSIM is enabled
        },
        // Add the missing stripeInfo object that's required for the Payment component
        stripeInfo: {
          activationFeePriceId: carrierData.activation_fee_price_id || ''
        }
      };

      console.log('Review data:', reviewData);
      setReviewData(reviewData);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderEsimActivationSteps = () => {
    return (
      <EsimInfoBox>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <AlertCircle size={22} color={theme.palette.primary.main} style={{ marginTop: '2px' }} />
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              eSIM Activation Process
            </Typography>

            <List dense disablePadding>
              <StepItem disableGutters>
                <ListItemIcon>
                  <CheckCircle size={18} color={theme.palette.primary.main} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 600 }}>Step 1:</Box> Your Account Owner will process your eSIM activation
                    </Typography>
                  }
                />
              </StepItem>

              <StepItem disableGutters>
                <ListItemIcon>
                  <MessageSquare size={18} color={theme.palette.primary.main} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 600 }}>Step 2:</Box> You'll receive a <Box component="span" sx={{ fontWeight: 600 }}>text message</Box> or <Box component="span" sx={{ fontWeight: 600 }}>banner notification</Box>
                    </Typography>
                  }
                />
              </StepItem>

              <StepItem disableGutters>
                <ListItemIcon>
                  <Zap size={18} color={theme.palette.primary.main} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 600 }}>Step 3:</Box> Follow the prompt instructions to complete setup
                    </Typography>
                  }
                />
              </StepItem>
            </List>
          </Box>
        </Box>
      </EsimInfoBox>
    );
  };

  const renderIMEIInstructions = () => {
    return (
      <InstructionAccordion>
        <AccordionSummary
          expandIcon={<ChevronDown size={20} />}
          sx={{ alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon size={20} color={theme.palette.primary.main} />
            <Typography sx={{ fontWeight: 500 }}>How to find your IMEI</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Your IMEI is a 15-digit unique identifier that helps verify device compatibility.
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <ImeiMethodContainer>
            <Phone size={20} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Using your dial pad:</Typography>
              <Typography variant="body2" gutterBottom>
                Enter <Box component="span" sx={{ fontWeight: 600 }}>*#06#</Box> on your phone's dial pad
              </Typography>
            </Box>
          </ImeiMethodContainer>

          <ImeiMethodContainer>
            <Smartphone size={20} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Android devices:</Typography>
              <Typography variant="body2" gutterBottom>
                Go to Settings → About device → Status
              </Typography>
            </Box>
          </ImeiMethodContainer>

          <ImeiMethodContainer>
            <Monitor size={20} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>iPhone devices:</Typography>
              <Typography variant="body2" gutterBottom>
                Go to Settings → General → About
              </Typography>
            </Box>
          </ImeiMethodContainer>

          <ImeiMethodContainer>
            <Tablet size={20} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Windows Phone:</Typography>
              <Typography variant="body2" gutterBottom>
                Go to Settings → About → More info
              </Typography>
            </Box>
          </ImeiMethodContainer>

          <Box sx={{ mt: 2, p: 1.5, backgroundColor: theme.palette.info.light, borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Note:</strong> For dual-SIM phones, you may have two IMEI numbers (IMEI1 and IMEI2).
              The IMEI2 field will appear if you select "Yes" to eSIM activation.
            </Typography>
          </Box>
        </AccordionDetails>
      </InstructionAccordion>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
              Will your phone work on this network?
            </Typography>
            <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4, maxWidth: '650px', mx: 'auto' }}>
              Your device must be unlocked. We recommend checking with the specific carrier
              to see if your device is compatible on their network.
              Optionally, type in your IMEI and we'll be happy to check for you.
            </Typography>

            <LineContainer>
              <SectionTitle color={theme.palette.primary.main}>
                Device Details
              </SectionTitle>

              <StyledTextField
                fullWidth
                label="IMEI"
                value={imei}
                onChange={handleIMEIChange}
                error={!isImeiValid}
                helperText={!isImeiValid ? 'Invalid IMEI (15 digits required)' : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Smartphone size={20} />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center" sx={{ mt: 1, mb: 1 }}>
                Interested in activating eSIM?
              </Typography>

              <RadioOptionBox>
                <RadioGroup
                  row
                  value={esim}
                  onChange={(e) => setEsim(e.target.value)}
                >
                  <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                </RadioGroup>
              </RadioOptionBox>

              <Collapse in={esim === 'yes'}>
                {renderEsimActivationSteps()}
                <StyledTextField
                  fullWidth
                  label="IMEI2 + EID"
                  value={imei2}
                  onChange={(e) => setImei2(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Smartphone size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Collapse>
            </LineContainer>

            {renderIMEIInstructions()}
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <LocationContainer>
              <SectionTitle color={theme.palette.info.main}>
                Location
              </SectionTitle>
              <Typography variant="body1" gutterBottom align="left" sx={{ mb: 3 }}>
                Please enter your ZIP code to get a new number. This helps us assign you a
                number that's appropriate for your area.
              </Typography>
              <StyledTextField
                fullWidth
                label="ZIP Code"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                error={!validateZipCode(zipCode) && zipCode !== ''}
                helperText={!validateZipCode(zipCode) && zipCode !== '' ? 'Invalid ZIP code (format: 12345 or 12345-6789)' : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MapPin size={20} />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="left" sx={{ mt: 4, mb: 2 }}>
                Preferred Area Code
              </Typography>
              <Typography variant="body1" gutterBottom align="left" sx={{ mb: 3 }}>
                Optional: If your ZIP code has multiple area codes, please enter your preferred area code.
                Subject to availability.
              </Typography>
              <StyledTextField
                fullWidth
                label="Area Code"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Hash size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </LocationContainer>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <SubscriptionAndShipping
              subscriptionType={subscriptionType}
              setSubscriptionType={setSubscriptionType}
              shippingMethod={shippingMethod}
              setShippingMethod={setShippingMethod}
              isEsim={esim === 'yes'}
            />
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <ReviewAndConfirm
              planDetails={reviewData?.planDetails}
              userDetails={reviewData?.userDetails}
              subscriptionDetails={reviewData?.subscriptionDetails}
              onEdit={handleBack}
              onProceedToPayment={handleNext}
            />
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Payment
              reviewData={reviewData}
              onBack={handleBack}
              onPaymentSuccess={(paymentResult) => {
                navigate('/success', {
                  state: {
                    ...reviewData,
                    paymentResult
                  }
                });
              }}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="get-new-number-wrapper">
        <StyledContainer>
          <ProgressIndicator mb={4}>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 1 }}>
              Estimated time to complete: 5 minutes
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#E5E7EB',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #ff385c 0%, #ff5c83 100%)',
                }
              }}
            />
          </ProgressIndicator>

          <StyledPaper sx={{ mt: 5 }}>
            <form onSubmit={(e) => e.preventDefault()}>
              <CustomStepper activeStep={activeStep} steps={steps} />

              <Box sx={{ px: { xs: 1, sm: 3 }, py: 3 }}>
                {renderStepContent(activeStep)}
              </Box>

              {/* Only show bottom navigation buttons if not on the Review & Confirm step (step 3) */}
              {activeStep !== 3 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 4,
                    flexDirection: isMobile ? 'column-reverse' : 'row',
                    gap: 2
                  }}
                >
                  <ActionButton
                    variant="outlined"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    startIcon={<ArrowLeft size={18} />}
                    isback="true"
                    fullWidth={isMobile}
                  >
                    Back
                  </ActionButton>

                  {activeStep !== steps.length - 1 && (
                    <ActionButton
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowRight size={18} />}
                      fullWidth={isMobile}
                    >
                      {activeStep === steps.length - 2 ? 'Proceed to Payment' : 'Next'}
                    </ActionButton>
                  )}
                </Box>
              )}
            </form>
          </StyledPaper>

          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
          />
        </StyledContainer>
      </div>
    </ThemeProvider>
  );
};

export default GetANewNumber;